import { useSearchParams, useLocation } from 'react-router-dom';
import React from 'react';

import useNavigate from './useNavigate';

export default function useQueryParams<
  TQueryParams extends Record<string, string | string[]>,
>() {
  const [queryParams, setQueryParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();

  const remove = React.useCallback(
    (key: keyof TQueryParams) => {
      const previous = new URLSearchParams(queryParams.toString());
      previous.delete(key as string);

      const newSearch = `?${previous.toString()}`;
      if (location.search !== newSearch) {
        navigate(`${location.pathname}${newSearch}` as any, { replace: true });
      }
    },
    [location.pathname, location.search, navigate, queryParams],
  );

  // ONE decode, and it is `URLSearchParams`'. This used to run
  // `decodeURIComponent` over a value that had ALREADY been decoded here,
  // which is wrong in two ways at once: a bare `%` (`?tab=100%`, a truncated
  // invite link) threw `URIError` from inside render — white-screening every
  // page that reads a query param, `AcceptInvitePage` included, since it reads
  // its hash from a `useMemo` — and `+` came back as `+` from one decoder and
  // as a space from the other. Nothing double-ENCODES on the write side
  // (`set`/`append` go through `URLSearchParams`; the hand-built search
  // strings in `educationViewerRoute` and `OrganizationOverview` encode once),
  // so there was never a second layer to peel.
  //
  // `|| null` keeps the ONE thing the old body got right: `?key=` is not a
  // value. Callers write `get(key) ?? undefined`, which passes `''` straight
  // through — so an empty param would reach a query key and an API call as an
  // empty string instead of being absent.
  const get = React.useCallback(
    (key: keyof TQueryParams) => queryParams.get(key as string) || null,
    [queryParams],
  );

  const set = React.useCallback(
    (key: keyof TQueryParams, value: null | string) => {
      const previous = new URLSearchParams(queryParams.toString());
      previous.set(key as string, value ?? '');

      const newSearch = `?${previous.toString()}`;

      if (location.search !== newSearch) {
        navigate(`${location.pathname}${newSearch}` as any, { replace: true });
      }
    },
    [location.pathname, location.search, navigate, queryParams],
  );

  const append = React.useCallback(
    (key: keyof TQueryParams, value: null | string) => {
      const previous = new URLSearchParams(queryParams.toString());
      previous.append(key as string, value ?? '');

      const newSearch = `?${previous.toString()}`;

      if (location.search !== newSearch) {
        navigate(`${location.pathname}${newSearch}` as any, { replace: true });
      }
    },
    [location.pathname, location.search, navigate, queryParams],
  );
  const setMultiple = React.useCallback(
    (args: Array<{ key: keyof TQueryParams; value: null | string }>) => {
      const previous = new URLSearchParams(queryParams.toString());
      args.forEach(({ key, value }) => {
        const queryValue = previous.get(key as string);
        if (!queryValue && queryValue !== '')
          previous.set(key as string, value ?? '');
      });
      const newSearch = `?${previous.toString()}`;
      if (location.search !== newSearch) {
        navigate(`${location.pathname}${newSearch}` as any, { replace: true });
      }
    },
    [location.pathname, location.search, navigate, queryParams],
  );

  return {
    append,
    get,
    queryParams,
    remove,
    set,
    setMultiple,
    setQueryParams,
  };
}
